import React from 'react'

import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Display,
  Heading1,
  JustifyContent,
  Space,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import type { HeroFields } from './types'

import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'

export type HeroProps = HeroFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

type RteNode = {
  type?: string
  uid?: string
  text?: string
  children?: RteNode[]
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

function isLeaf(node: RteNode): node is RteNode & { text: string } {
  return typeof node.text === 'string'
}

function RichTextLeaf({ node }: { node: RteNode & { text: string } }) {
  let content: React.ReactNode = node.text
  if (node.bold) {
    content = <Bold>{content}</Bold>
  }
  if (node.italic) {
    content = <em>{content}</em>
  }
  if (node.underline) {
    content = <u>{content}</u>
  }
  return <>{content}</>
}

function RichTextChildren({ nodes }: { nodes: RteNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        const key = node.uid ?? `node-${index}`
        if (isLeaf(node)) {
          return <RichTextLeaf key={key} node={node} />
        }
        return <RichTextChildren key={key} nodes={node.children ?? []} />
      })}
    </>
  )
}

function getRichTextParagraphs(body?: HeroFields['body']) {
  if (!body) {
    return []
  }

  const root = body as RteNode

  return (root.children ?? []).filter(
    (block) => block.type === 'p' || (block.children?.length ?? 0) > 0,
  )
}

function HeroBody({ body }: { body?: HeroFields['body'] }) {
  const paragraphs = getRichTextParagraphs(body)

  if (paragraphs.length === 0) {
    return null
  }

  return (
    <Box paddingBottom={[4]} paddingRight={[null, 4]}>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={paragraph.uid ?? `p-${index}`}>
          {index > 0 && <Space size={4} />}
          <Body2 size={4} textAlign={TextAlign.Center} style={{ whiteSpace: 'pre-wrap' }}>
            <RichTextChildren nodes={paragraph.children ?? []} />
          </Body2>
        </React.Fragment>
      ))}
    </Box>
  )
}

export function Hero({
  heading,
  body,
  cta,
  onTriggerPromocode,
  image,
  image_alt,
  image_mobile,
}: HeroProps) {
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)

  return (
    <Box>
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        paddingH={[4, 8, 20]}
        paddingV={[8, 16]}
        paddingBottom={[12, 16]}
      >
        <Box maxWidth="512px">
          <Box paddingBottom={3}>
            <Heading1 textAlign={TextAlign.Center}>{heading}</Heading1>
          </Box>

          <HeroBody body={body} />

          {ctaLink && (
            <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
              <CtaLink cta={ctaLink} />
            </Box>
          )}
        </Box>
      </Box>

      {image?.url && (
        <ContentstackImage
          fit="cover"
          width="100%"
          height={['120px', '141px', null, '160px']}
          sizes="100vw"
          alt={image_alt || ''}
          src={image.url}
          mobileSrc={image_mobile?.url}
        />
      )}
    </Box>
  )
}
